import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Plus, MessageSquare, Heart, Pin, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Loading, EmptyState } from "@/components/shared";
import api from "@/lib/api";

const SPACE_NAMES = {
  "introductions": "Introductions", "ask-an-expert": "Ask an Expert", "ai-automation": "AI & Automation",
  "marketing-sales": "Marketing & Sales", "ecommerce-growth": "E-commerce Growth", "operations-sops": "Operations & SOPs",
  "weekly-accountability": "Weekly Accountability", "member-wins": "Member Wins", "resource-requests": "Resource Requests",
};

export default function CommunitySpace() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["posts", slug],
    queryFn: async () => (await api.get("/community/posts", { params: { space: slug } })).data,
    retry: false,
  });

  const create = async () => {
    if (!form.title || !form.body) return;
    try {
      await api.post("/community/posts", { space: slug, ...form });
      toast.success("Posted!"); setOpen(false); setForm({ title: "", body: "" }); refetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post"); }
  };

  const locked = error?.response?.status === 403;

  return (
    <DashboardLayout>
      <button onClick={() => navigate("/community")} className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-navy" data-testid="space-back"><ArrowLeft className="h-4 w-4" /> All spaces</button>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-navy sm:text-3xl">{SPACE_NAMES[slug] || slug}</h1>
        {!locked && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="new-post-btn"><Plus className="mr-1.5 h-4 w-4" /> New post</Button></DialogTrigger>
            <DialogContent data-testid="new-post-dialog">
              <DialogHeader><DialogTitle>Create a post</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="post-title-input" />
                <Textarea placeholder="Share your question, win or update…" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="post-body-input" />
              </div>
              <DialogFooter><Button onClick={create} className="bg-emerald text-white hover:bg-emerald-700" data-testid="post-submit-btn">Post</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {isLoading ? <Loading /> : locked ? (
        <div className="rounded-xl border border-emerald/30 bg-emerald/5 p-8 text-center" data-testid="space-locked">
          <div className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-lg bg-emerald/10 text-emerald"><Lock className="h-5 w-5" /></div>
          <h3 className="mt-4 font-heading text-lg font-semibold text-navy">This is a premium space</h3>
          <p className="mt-2 text-sm text-slate-600">Upgrade your membership to join this conversation.</p>
          <Button onClick={() => navigate("/pricing")} className="mt-4 rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="space-upgrade-btn">See plans</Button>
        </div>
      ) : (data?.length ? (
        <div className="space-y-3">
          {data.map((p) => (
            <Link key={p.id} to={`/community/post/${p.id}`} data-testid={`post-${p.id}`}
              className="block rounded-xl border border-slate-200 bg-white p-5 transition-colors hover:bg-slate-50">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {p.pinned && <Pin className="h-3.5 w-3.5 text-emerald" />}
                    <h3 className="truncate font-heading text-lg font-semibold text-navy">{p.title}</h3>
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm text-slate-600">{p.body}</p>
                  <p className="mt-3 text-xs font-medium text-slate-400">by {p.author_name}</p>
                </div>
                <div className="flex flex-shrink-0 gap-4 text-xs font-medium text-slate-400">
                  <span className="flex items-center gap-1"><Heart className="h-4 w-4" /> {p.reactions}</span>
                  <span className="flex items-center gap-1"><MessageSquare className="h-4 w-4" /> {p.comment_count}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : <EmptyState title="No posts yet" subtitle="Be the first to start a conversation here." />)}
    </DashboardLayout>
  );
}
